import { isUiModeExpert, isUiModeProductAdmin } from "../uiMode";
import type Button from "./Button";
import type Channel from "./Channel";
import { filterChannelsByUserRole } from "./Channel";
import { ChannelType, channelTypeInfo, specialChannelTypes } from "./ChannelType";
import type Device from "./Device";
import { sortArrayByProperty } from "./utils";

export function channelsSortedForDisplayFromDevice(
  device: Device,
  show?: {
    buttonChannels?: boolean;
    actorChannels?: boolean;
    statusChannels?: boolean;
    logicChannels?: boolean;
    environmentChannel?: boolean;
    temperatureMeasurementChannel?: boolean;
    withoutKnxChannel?: boolean;
    generalChannel?: boolean;
    specialChannel?: boolean;
    patchChannels?: boolean;
    longPressChannels?: boolean;
    manualChannel?: boolean;
    unknownChannel?: boolean;
    ignoreChannel?: boolean;
    defaultSortingAndFiltering?: boolean;
  },
): Array<Channel> {
  const {
    buttonChannels,
    actorChannels,
    statusChannels,
    logicChannels,
    withoutKnxChannel,
    environmentChannel: showEnvironmentChannel,
    temperatureMeasurementChannel: showTemperatureMeasurementChannel,
    generalChannel: showGeneralChannel,
    specialChannel: showSpecialChannel,
    patchChannels,
    longPressChannels,
    manualChannel: showManualChannel,
    unknownChannel: showUnknownChannel,
    ignoreChannel: showIgnoreChannel,
    defaultSortingAndFiltering,
  } = show || {};
  if (channelTypeInfo(device.channelType).isSupply) {
    //
    // SupplyChannels are sorted by consumer fulltag
    //
    return device.channels.asArray().sort((a, b) => {
      const c1 = a.getConnectedConsumer();
      const c2 = b.getConnectedConsumer();
      if (c1 && c2) {
        return c1.fullTag.localeCompare(c2.fullTag, "en", { numeric: true }); // sort A1, A2, A10 korrect
      }

      return a.tag.localeCompare(b.tag, "en", { numeric: true }); // sort A1, A2, A10 korrect
    });
  }

  const childChannelsButton = new Array<Channel>();
  const childChannelsStatus = new Array<Channel>();
  const childChannelsActor = new Array<Channel>();
  const childChannelsLogicInput = new Array<Channel>();
  const childChannelsLogicOuput = new Array<Channel>();

  const childChannelsWithoutKnxChannel = new Array<Channel>();

  let environmentChannel: Channel | undefined;
  let temperatureMeasurementChannel: Channel | undefined;

  const rawChannels =
    device.isButton && !isUiModeProductAdmin()
      ? (device as any as Button).channelsWithoutRockerValueHidden
      : device.channels.asArray();

  const sortedChannels = sortArrayByProperty(rawChannels, "tag");

  sortedChannels.forEach((channel) => {
    if (
      (channel.channelType != ChannelType.General || showGeneralChannel) &&
      (channel.channelType != ChannelType.Unknown || !channel.knxChannel)
    ) {
      if (!channel.knxChannel) {
        childChannelsWithoutKnxChannel.push(channel);
      } else if (defaultSortingAndFiltering && specialChannelTypes.includes(channel.channelType)) {
        // if default sorting and filtering is active, special channels are not shown
      } else if (channel.isStatusChannel) {
        childChannelsStatus.push(channel);
      } else if (channel.isLogicInputChannel) {
        childChannelsLogicInput.push(channel);
      } else if (channel.isLogicOutputChannel) {
        childChannelsLogicOuput.push(channel);
      } else if (channel.channelType == ChannelType.Environment) {
        environmentChannel = channel;
      } else if (channel.channelType == ChannelType.TemperatureMeasurement && !temperatureMeasurementChannel) {
        temperatureMeasurementChannel = channel;
      } else if (channel.tag.startsWith("TP") && patchChannels == false) {
        // dont show patch channels if not wanted
      } else if (
        (channel.tag.startsWith("_l") || //long press
          channel.tag.startsWith("_x")) && // extra long press
        longPressChannels == false
      ) {
        // dont show long press channels if not wanted
      } else if (
        channel.channelType == ChannelType.Special &&
        (showSpecialChannel == false || defaultSortingAndFiltering)
      ) {
        // dont show special channels if not wanted
      } else if (
        channel.channelType == ChannelType.ManualSignals &&
        (showManualChannel == false || defaultSortingAndFiltering)
      ) {
        // dont show manual channels if not wanted
      } else if (
        (channel.channelType == ChannelType.Unknown || channel.channelType == ChannelType.UnknownMapWithScript) &&
        (showUnknownChannel == false || defaultSortingAndFiltering)
      ) {
        // dont show unknown channels if not wanted
      } else if (
        channel.channelType == ChannelType.Ignored &&
        (showIgnoreChannel == false || defaultSortingAndFiltering)
      ) {
        // dont show ignore channels if not wanted
      } else if (channel.isActorChannel) {
        childChannelsActor.push(channel);
      } else {
        // look here if buttonchannel is patch channel

        childChannelsButton.push(channel);
      }
    }
  });

  let res = new Array<Channel>();
  if (buttonChannels || buttonChannels == undefined) {
    res = res.concat(childChannelsButton);
  }

  if (temperatureMeasurementChannel && showTemperatureMeasurementChannel) {
    res.push(temperatureMeasurementChannel);
  }
  if (statusChannels || statusChannels == undefined) {
    res = res.concat(childChannelsStatus);
  }
  if (actorChannels || actorChannels == undefined) {
    res = res.concat(childChannelsActor);
  }
  if (logicChannels || logicChannels == undefined) {
    res = res.concat(childChannelsLogicInput);
    res = res.concat(childChannelsLogicOuput);
  }
  if (withoutKnxChannel || withoutKnxChannel == undefined) {
    res = res.concat(childChannelsWithoutKnxChannel);
  }

  if (environmentChannel) {
    if (showEnvironmentChannel) {
      res.push(environmentChannel);
    }
  }

  res = filterChannelsByUserRole(device, res);

  return res;
}
